import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { gql, useLazyQuery, useMutation } from "@apollo/client";

import { useLocation, useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { SpinnerView } from "../elements/Spinners";
import { LOGOUT } from "../queries/auth";
import { get } from "lodash";
import { areTermsAndConditionsUpToDate } from "../utils/tos";
import * as Sentry from "@sentry/react";

export interface Session {
  user?: User;
  initialized: boolean;
  id: string;
}

export interface User {
  id: string;
  email: string;
  first_name: string;
  other_names: string;
  initialized: boolean;
  country?: { id: string; name: string };
  role?: string;
  organization_raw?: string;
  organization_raw_type?: string;
  system_user_type?: string;
  terms_conditions_accepted?: boolean;
  terms_conditions_accepted_at?: string;
  claims?: any[];
  metadata?: any;
}

export interface AuthContextType {
  session?: Session;
  loading: boolean;
  error?: any;
  check: any;
  setSession: any;
  // login: (email: string, password: string) => void;
  // signUp: (email: string, name: string, password: string) => void;
  logout: any;
  isLoggingOut: boolean;
  user: any;
  getUser: () => void;
  userLoading: boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

const CHECK_AUTH = gql`
  query auth {
    auth {
      id
      has_active_subscription
      user {
        id
        first_name
        other_names
        organization_raw
        organization_raw_type
        role
        has_active_subscription
        system_user_type
        terms_conditions_accepted
        terms_conditions_accepted_at
        profile_image_url
        country {
          id
          name
        }
        claims {
          id
          status
          organization {
            id
            name
            profile_image_url
            sectors {
              id
              name
            }
            socials {
              website
            }
          }
        }
        following {
          id
          name
        }
        email
        metadata {
          id
          preferred_countries {
            id
            name
          }
          preferred_products {
            id
            name
          }
          preferred_sectors {
            id
            name
          }
        }
      }
      seats {
        id
        plan {
          name
        }
        subscription {
          status
          current_period_end
        }
        billing_organization {
          name
        }
      }
    }
  }
`;

export const GET_CURRENT_USER = gql`
  query {
    currentUser {
      id
      first_name
      other_names
      has_active_subscription
      is_tour_completed
      profile_image_url
      email
      bb_newsletter_sub
      ag_newsletter_sub
      last_password_update
      email_verified
      system_user_type
      terms_conditions_accepted
      metadata {
        id
        preferred_countries {
          id
          name
        }
        preferred_products {
          id
          name
        }
        preferred_sectors {
          id
          name
        }
        preferred_categories
        preferred_frequency
        preferred_channels
      }
      following {
        id
        name
      }
      claims {
        id
        status
        organization {
          id
          name
          profile_image_url
          sectors {
            id
            name
          }
          socials {
            website
          }
        }
      }
      organization_raw
      role
    }
  }
`;

export function AuthProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [session, setSession] = useState<Session>({
    id: "",
    initialized: false,
    user: undefined,
  });
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);
  const [hasNavigatedToOnboarding, setHasNavigatedToOnboarding] = useState<boolean>(false);
  const [auth, { loading, data, error, refetch }] = useLazyQuery(CHECK_AUTH, {
    fetchPolicy: "network-only",
  });
  const [getUser, { data: user, loading: userLoading }] = useLazyQuery(
    GET_CURRENT_USER,
    {
      fetchPolicy: "network-only",
    },
  );
  const [requestLogout, {}] = useMutation(LOGOUT);

  const navigate = useNavigate();

  const check = useCallback(
    (redirect: string) => {
      try {
        return auth().then(() => {
          if (error === undefined) {
            setSession({ ...data.auth, initialized: true });
            navigate(redirect);
            return true;
          } else {
            return false;
          }
        });
      } catch (e) {
        // error state
      }
    },
    [auth, error, data, navigate, setSession]
  );

  const logout = useCallback(() => {
    setIsLoggingOut(true);
    requestLogout().then(() => {
      window.location.href = "/";
      setIsLoggingOut(false);
    });
  }, [requestLogout, setIsLoggingOut]);

  useEffect(() => {
    auth().then((r) => {
      getUser();
      if (!r.data && location.pathname.substring(1) != "") {
        setSearchParams({ redirect: location.pathname.substring(1) });
      }
      setSession({ ...r.data?.auth, initialized: true });
      if (searchParams.get("redirect")) {
        navigate(searchParams.get("redirect") || "/");
      }
    });
  }, []);

  useEffect(() => {
    const userId = get(session, "user.id", undefined);
    if (userId) {
      //@ts-ignore
      window.dataLayer.push({
        event: "set_user_id",
        user_id: userId,
      });
    }
  }, [session.user?.id]); // Cleaner dependency

  // Check if user needs to complete onboarding
  const needsOnboarding = useMemo(
    () =>
      !loading &&
      session?.user &&
      location.pathname !== "/terms-of-use" &&
      location.pathname !== "/onboarding" &&
      (!session.user.country ||
        !session.user.role ||
        !session.user.organization_raw ||
        !session.user.organization_raw_type ||
        !session.user.system_user_type ||
        !session.user.terms_conditions_accepted ||
        !areTermsAndConditionsUpToDate(
          session.user.terms_conditions_accepted_at,
        )),
    [loading, session?.user, location.pathname]
  );

  // Handle onboarding navigation with useEffect instead of Navigate component
  useEffect(() => {
    if (needsOnboarding && !hasNavigatedToOnboarding) {
      setHasNavigatedToOnboarding(true);
      navigate("/onboarding", { replace: true });
    }
    // Reset flag when user completes onboarding or location changes away from onboarding
    if (!needsOnboarding && hasNavigatedToOnboarding) {
      setHasNavigatedToOnboarding(false);
    }
  }, [needsOnboarding, hasNavigatedToOnboarding, navigate]);

  // Memoize the extracted user value to stabilize it
  const currentUser = useMemo(() => get(user, "currentUser", undefined), [user?.currentUser?.id]);

  // Set Sentry user context when user logs in
  useEffect(() => {
    if (currentUser?.id) {
      Sentry.setUser({
        id: currentUser.id,
        email: currentUser.email,
        username: `${currentUser.first_name} ${currentUser.other_names}`.trim(),
      });
    } else {
      // Clear user context when logged out
      Sentry.setUser(null);
    }
  }, [currentUser?.id, currentUser?.email, currentUser?.first_name, currentUser?.other_names]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      session,
      error,
      loading,
      check,
      setSession,
      logout,
      isLoggingOut,
      user: currentUser,
      getUser,
      userLoading,
    }),
    [session, error, loading, check, logout, isLoggingOut, currentUser, userLoading]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {loading && !session.initialized && !userLoading && (
        <SpinnerView message="Checking current session..." />
      )}
      {isLoggingOut && <SpinnerView message="Logging out..." />}
      {!loading && session.initialized && !needsOnboarding && !isLoggingOut && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function useAuthContext() {
  return useContext(AuthContext);
}
